import _ from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { readRemoteFile } from 'react-papaparse'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

export type CsvViewerProps = {
  onError?: (error?: any) => void
  src: string
}

export default function CsvViewer({ onError, src }: CsvViewerProps) {
  const { t } = useTranslation()
  const [data, setData] = useState<string[][]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const columns: { id: string, label: string }[] = useMemo(() => {
    return _.map(data[0], (field, index) => ({
      id: field + index,
      label: field
    }))
  }, [data])

  const rows = useMemo(() => {
    return _.tail(data)
  }, [data])

  useEffect(() => {
    readRemoteFile<string[]>(src, {
      complete: (results) => {
        setData(results.data)
        setLoading(false)
      },
      error: (error) => {
        setLoading(false)
        setError(error)
        onError?.(error)
      },
      download: true
    })
    // eslint-disable-next-line
  }, [src])

  if (loading) {
    return (
      <CircularProgress />
    )
  }

  return (
    <Box sx={{ margin: '0 auto' }}>
      {!!error && (
        <Typography>{t('error.file.failedToLoad.message')}</Typography>
      )}
      {!error && !!data.length && (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} sx={{ maxWidth: '200px' }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, rowIdx) => {
                    return (
                      <TableRow hover key={rowIdx}>
                        {row.map((cell: string, cellIdx) => {
                          return (
                            <TableCell key={rowIdx + '-' + cellIdx} sx={{ maxWidth: '200px', wordBreak: 'break-word' }}>
                              {cell}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 15, 20]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
      )}
    </Box>
  )
}